import { capitalize } from '@karehero/helpers/string';

import { CARE_RECIPIENT_PLACEHOLDER } from 'config/benefitsCalculatorV2Steps';
import { splitFullName } from 'utils/splitFullName';

/** Who something is about: the person they look after, or themselves. */
export interface Person {
  isSelf: boolean;
  /** Their first name, once we have it. */
  name?: string;
}

/** What we call them, from their full name. */
export const firstNameOf = (fullName?: string) =>
  (fullName && splitFullName(fullName).firstName) || undefined;

/** Their name, or how we refer to them without it. */
export const nameOf = (person: Person) =>
  person.isSelf ? 'you' : (person.name ?? 'the person you look after');

/** Whose something is, mid-line. */
export const possessive = (person: Person) =>
  person.isSelf ? 'your' : person.name ? `${person.name}'s` : 'their';

/** Whose something is, starting a line. */
export const whose = (person: Person) => capitalize(possessive(person));

/** Whose something is, without naming them. */
export const theirs = (person: Person) => (person.isSelf ? 'your' : 'their');

/** A line said about them in either voice: to them, or about the person they look after. */
export interface Copy {
  self: string;
  other: string;
}

/** Stands in for their name, or how we refer to them without it; the calculator's own. */
export const NAME_PLACEHOLDER = CARE_RECIPIENT_PLACEHOLDER;
/** Stands in for whose something is, mid-line and starting a line. */
export const POSSESSIVE_PLACEHOLDER = '{their}';
export const WHOSE_PLACEHOLDER = '{Their}';

/** The copy in the voice for them, with their name filled in. */
export const phrase = (copy: Copy, person: Person) =>
  (person.isSelf ? copy.self : copy.other)
    .replaceAll(NAME_PLACEHOLDER, nameOf(person))
    .replaceAll(POSSESSIVE_PLACEHOLDER, possessive(person))
    .replaceAll(WHOSE_PLACEHOLDER, whose(person));
